import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/utils/constants";

/** React StrictMode dev ortamında effect iki kez çalışır — tek redeem isteği */
const adminLinkRedeemPromises = new Map();

const redeemAdminLoginLink = (token) => {
  const key = String(token || "").trim();
  if (!key) return Promise.reject(new Error("missing token"));
  if (adminLinkRedeemPromises.has(key)) {
    return adminLinkRedeemPromises.get(key);
  }
  const promise = axios.get(`${API}/auth/admin-login-link`, { params: { token: key } })
    .then((res) => res.data || {})
    .finally(() => {
      window.setTimeout(() => adminLinkRedeemPromises.delete(key), 30000);
    });
  adminLinkRedeemPromises.set(key, promise);
  return promise;
};

const completeAdminLinkLogin = (jwt) => {
  sessionStorage.removeItem("auth_manual_logout");
  localStorage.setItem("token", jwt);
  axios.defaults.headers.common.Authorization = `Bearer ${jwt}`;
};

/**
 * /giris/:token — süper admin tarafından oluşturulan tek kullanımlık giriş linki
 */
const AdminLoginLinkPage = ({ isDark = true }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!token) {
        setError("Geçersiz giriş bağlantısı.");
        return;
      }

      const cachedJwt = sessionStorage.getItem(`admin_link_jwt:${token}`);
      if (cachedJwt) {
        completeAdminLinkLogin(cachedJwt);
        if (!cancelled) {
          setSuccess(true);
          window.location.href = "/cdr";
        }
        return;
      }

      try {
        const data = await redeemAdminLoginLink(token);
        if (!data.ok || !data.token) {
          if (!cancelled) setError(data.message || "Giriş bağlantısı kullanılamadı.");
          return;
        }
        sessionStorage.setItem(`admin_link_jwt:${token}`, data.token);
        completeAdminLinkLogin(data.token);
        if (!cancelled) {
          setSuccess(true);
          window.setTimeout(() => {
            window.location.href = "/cdr";
          }, 400);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || "Giriş bağlantısı geçersiz veya süresi dolmuş.");
        }
      }
    };

    run();
    return () => { cancelled = true; };
  }, [token]);

  const bgClass = isDark ? "bg-slate-950 text-slate-100" : "bg-slate-100 text-slate-900";
  const cardClass = isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200";
  const mutedClass = isDark ? "text-slate-400" : "text-slate-500";

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${bgClass}`}>
      <div className={`w-full max-w-md rounded-2xl border p-6 shadow-xl ${cardClass}`}>
        {!error && !success ? (
          <div className="flex flex-col items-center text-center gap-4 py-6">
            <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
            <div>
              <h1 className="text-lg font-semibold">Panele giriş yapılıyor</h1>
              <p className={`text-sm mt-1 ${mutedClass}`}>Kullanıcı yetkileriyle oturum açılıyor…</p>
            </div>
          </div>
        ) : null}

        {success ? (
          <div className="flex flex-col items-center text-center gap-3 py-4">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" />
            <h1 className="text-lg font-semibold">Giriş başarılı</h1>
            <p className={`text-sm ${mutedClass}`}>Panel açılıyor…</p>
          </div>
        ) : null}

        {error ? (
          <div className="flex flex-col items-center text-center gap-4 py-4">
            <ShieldAlert className="w-12 h-12 text-rose-500" />
            <h1 className="text-lg font-semibold">Giriş yapılamadı</h1>
            <p className={`text-sm ${mutedClass}`}>{error}</p>
            <Button type="button" variant="outline" onClick={() => navigate("/login4", { replace: true })}>
              Giriş sayfasına dön
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AdminLoginLinkPage;
